/**
 * What the Data step produced → which slot each piece fills (CT-5641).
 *
 * The builder collects three ordered pools — the measures, the groupers and
 * the exposed columns — plus the slots the author answered by name in the
 * Configure step. This decides which slot each one belongs to, reading the
 * type's own contract through `widgetSlotModel`, and hands the result to
 * `buildWidgetBindings`, which turns it into the wire shape.
 *
 * It replaces `summaryBindings`, whose job was the same for the v1 contract
 * and which could only ever place **one** aggregation: the Data step emitted
 * a single `COUNT_ROWS` and a slot wanting money refused it. rev4 removes
 * that ceiling — the aggregate travels on the binding now — so the measures
 * are a pool like the other two, and the day the Data step grows a measure
 * picker this module needs no change.
 *
 * ## The rules, in order
 *
 * 1. **An answer beats a guess.** A slot the author assigned by name takes
 *    that assignment. Two kinds are never filled any other way:
 *
 *    - **A named column slot.** Which column is the board's lane, or the
 *      alert's severity, is a meaning only the author has; binding whichever
 *      came first publishes a board whose lanes mean nothing.
 *    - **An optional measure slot.** A second measure is not a budget, a
 *      target or a maximum because it happened to be next — `COST_KPI.budget`
 *      and `INDICATOR.max` change what the widget *says*. The v1 builder drew
 *      the same line: its optional pass considered dimensions alone.
 *
 *    Optional **dimension** slots are the exception, and the reason the rule
 *    is worth stating: a second grouper on `series` is the same grouping the
 *    author already asked for, split one level further.
 * 2. **The record key is assigned, never asked.** A slot accepting `UUID`
 *    alone has exactly one candidate.
 * 3. **Required before optional.** The first grouper fills the first required
 *    dimension slot, the second the next; a stacked bar's `series` is
 *    required and a bar chart's is not, and the axes must not swap because of
 *    it. Optional slots take what the required ones left.
 * 4. **A pool item that does not fit its turn's slot stays in the pool.**
 *    `FUNNEL.stage_order` accepts `INTEGER` alone; handing it a text grouper
 *    would publish a type mismatch, so it is offered, refused, and the
 *    grouper waits for a slot that takes it.
 * 5. **Required lists take all they can; one optional list takes the rest.**
 *    A table's `columns` is required and capped at 40, so it takes every
 *    exposed column. A profile card's `identity` is required and capped at
 *    three, so it takes the first three and `fact` — the first optional list
 *    of the same role — absorbs the remainder. No *other* optional list is
 *    filled from the pool: putting a leftover column in `signal` would
 *    publish it as a signal, a meaning the author never gave it.
 */

import { WIDGET_FIELD_ROLES } from '../constants/widgetContract.js';
import { readSlotModel } from './widgetSlotModel.js';
import { dataTypeFits } from './widgetDataTypes.js';

const EMPTY = Object.freeze([]);

/**
 * A pool item fits a slot when the type it **produces** is one the slot
 * accepts. For a column that is its own stored type; for a measure it is the
 * aggregate's output, which the caller resolves before filling the pool (see
 * `aggregateOutputType`) — `SUM` over an integer is still an integer, and a
 * money slot refuses it.
 */
const fits = (item, slot) => dataTypeFits(item?.dataType, slot?.dataTypes ?? EMPTY);

/** Take the first item of the pool that fits, or nothing. */
const takeFitting = (pool, slot) => {
  const index = pool.findIndex((item) => fits(item, slot));
  if (index === -1) return null;
  return pool.splice(index, 1)[0];
};

/** How many a list may still hold. */
const capacity = (slot, held = 0) => Math.max(0, (slot.max ?? Infinity) - held);

const roleOf = (slot) => {
  if (slot.roles.includes(WIDGET_FIELD_ROLES.AGGREGATE)) return WIDGET_FIELD_ROLES.AGGREGATE;
  if (slot.roles.includes(WIDGET_FIELD_ROLES.DIMENSION)) return WIDGET_FIELD_ROLES.DIMENSION;
  return WIDGET_FIELD_ROLES.COLUMN;
};

/**
 * @param {object} params
 * @param {object|null} params.configSchema The type's `configuration_schema`.
 * @param {Array<{ field: string, aggregate: string, dataType?: string, label?: string }>}
 *   [params.measures] The aggregations, in the order the author added them.
 * @param {Array<{ field: string, dataType?: string, timeGrain?: string }>}
 *   [params.dimensions] The groupers, in order.
 * @param {Array<{ field: string, dataType?: string, label?: string }>}
 *   [params.columns] The exposed columns, in order.
 * @param {{ field: string }|null} [params.recordKey] The record uuid's alias.
 * @param {object} [params.authored] `{ [slotKey]: assignment | assignment[] }`
 *   — what the author answered by name.
 * @returns {object} `{ [slotKey]: assignment | assignment[] }`, ready for
 *   `buildWidgetBindings`. A slot nothing fills is absent.
 */
export function assignSlotsFromDataStep({
  configSchema,
  measures = EMPTY,
  dimensions = EMPTY,
  columns = EMPTY,
  recordKey = null,
  authored = {},
}) {
  const model = readSlotModel(configSchema);
  if (!model.slots.length) return {};

  // Copies: the pools are drained as slots take from them.
  const pools = {
    [WIDGET_FIELD_ROLES.AGGREGATE]: measures.filter((item) => item?.field || item?.aggregate),
    [WIDGET_FIELD_ROLES.DIMENSION]: dimensions.filter((item) => item?.field),
    [WIDGET_FIELD_ROLES.COLUMN]: columns.filter((item) => item?.field),
  };

  const assignments = {};
  const ordered = [
    ...model.slots.filter((slot) => slot.required),
    ...model.slots.filter((slot) => !slot.required),
  ];

  const take = (slot) => {
    const pool = pools[roleOf(slot)];
    if (!slot.isArray) return takeFitting(pool, slot);

    const items = [];
    while (items.length < capacity(slot, assignments[slot.key]?.length ?? 0)) {
      const item = takeFitting(pool, slot);
      if (!item) break;
      items.push(item);
    }
    return items.length ? items : null;
  };

  /*
    A column the author assigned by name is spent.

    Two slots on the same column cannot both be honoured: each binding needs
    its own `field_key`, and in `ENTITY` mode that key has to be the alias of
    an element the generated dataset carries — so the second one names a
    column that does not exist (`UI_BINDING_FIELD_MISSING (card_fields
    department_2)`, dev 2026-10-20). Taking the authored columns out of the
    pool is what stops a list slot from re-using the board's own lane.
  */
  const spend = (value) => {
    const items = Array.isArray(value) ? value : [value];
    items.forEach((item) => {
      // Every pool, not only the columns: since the author answers measure
      // and dimension slots too (fix/widgets), a measure placed in `value`
      // must not also be taken by `target` — same key twice is the same
      // refusal.
      Object.values(pools).forEach((pool) => {
        const at = pool.findIndex((candidate) => candidate.field === item?.field);
        if (at !== -1) pool.splice(at, 1);
      });
    });
  };

  ordered.forEach((slot) => {
    if (Object.hasOwn(authored, slot.key)) {
      assignments[slot.key] = authored[slot.key];
      spend(authored[slot.key]);
      return;
    }
    if (slot.isRecordKey) {
      if (recordKey?.field) assignments[slot.key] = recordKey;
      return;
    }
    /*
      Rule 1. A named column is the author's to answer at any time; an
      optional measure is theirs once the required ones are served. The
      record key above is the exception, and a list is the author's own
      ordered selection rather than a question about meaning.
    */
    if (!slot.isArray && roleOf(slot) === WIDGET_FIELD_ROLES.COLUMN) return;
    if (!slot.isArray && !slot.required
      && roleOf(slot) === WIDGET_FIELD_ROLES.AGGREGATE) return;
    // An optional list waits: the required ones are served first, and then
    // exactly one of them is offered what is left (rule 5).
    if (slot.isArray && !slot.required) return;

    const value = take(slot);
    if (value) assignments[slot.key] = value;
  });

  const overflow = ordered.find((slot) => slot.isArray
    && !slot.required
    && !Object.hasOwn(authored, slot.key)
    && pools[roleOf(slot)].length > 0);
  if (overflow) {
    const value = take(overflow);
    if (value) assignments[overflow.key] = value;
  }

  return assignments;
}

export default assignSlotsFromDataStep;
